<script setup lang="ts">
  /**
   * This component dynamically renders the appropriate ad component based on the provided ad type and placement from props in ContentDisplaySettings and $Filters(user geo/data).
   * It can be used universally in the code base instead of calling direct ad provider components for easier A/B testing.
   */
  import { computed, withDefaults, defineProps } from 'vue'
  import { AdOpsOutbrain, ArticleCard } from '#components'
  import Taboola from '../AdOps/Taboola.vue'
  import Assertive from '../AdOps/Assertive.vue'
  import type { $Filters } from '../../types'
  import type { AdPlacement } from '../../ad_ops/adPlacementMappings'
  import { createExtrnId } from '../../utils/string/uuid'
  import { userEngagementStore } from '../../store/userData/UserEngagementStore'

  import debug from '../../utils/console'

  const console = debug.extend('AdHandler')

  const providerMap: Record<string, Component> = {
    assertive: Assertive,
    outbrain: AdOpsOutbrain,
    taboola: Taboola,
    direct: ArticleCard,
  }

  export interface AdHandlerProps {
    ad?: boolean
    placement: string
    adType?: string | null
    adHeight?: number
    adWidth?: number
    design?: ArticleCardProps['design']
    variant?: ArticleCardProps['variant']
    tilePosition?: number
  }

  const props = withDefaults(defineProps<AdHandlerProps>(), {
    ad: true,
    adType: 'prog',
    adHeight: 304,
    adWidth: 300,
    design: 'msn',
    variant: 'default',
    tilePosition: -1,
  })

  const filters: $Filters = useNuxtApp().$filters
  const userEngagement = userEngagementStore()
  const appConfig = useAppConfig()

  const style = computed(() => getArticleStyleVariants(props))

  const adProvider = computed(() => {
    switch (props.adType) {
      case 'prog':
        return 'assertive'
      case 'native':
        return filters.nativeProvider || 'outbrain'
      default:
        return props.adType
    }
  })

  const component = computed(() => providerMap[adProvider.value] || ArticleCard)

  const componentProps = computed(() => {
    const placementConfigs = appConfig.adops.placements[props.placement]

    if (!placementConfigs) {
      console.error(`Missing ad placement configs for "${props.placement}".`)
    }

    const isValidAdType = (key: string): key is keyof AdPlacement =>
      key in (placementConfigs || {})

    const adProviderConfig = isValidAdType(adProvider.value)
      ? placementConfigs?.[adProvider.value]
      : { ...props }

    if (!adProviderConfig) {
      console.error(
        `Missing ad placement configs for "${props.placement}" placement and "${adProvider.value}" provider.`
      )
    }
    const adInstanceId: string = createExtrnId()
    return {
      ...adProviderConfig,
      ...props,
      class: style.value.wrapper,
      adInstanceId,
    }
  })
  const handleClick = () => {
    userEngagement.setUserEngagement('isEngaged', true)
  }
</script>

<template>
  <component
    :is="component"
    :key="componentProps.adInstanceId"
    v-bind="componentProps"
    aria-label="Partner Content"
    role="article"
    :style="{
      width: `${componentProps.adWidth}px`,
      height: `${componentProps.adHeight}px`,
    }"
    @click="handleClick"
  />
</template>
